import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import ContactForm from '../components/ContactForm';
import './Home.css';

function Home() {
  const [modalOpen, setModalOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, [location]);

  return (
    <div className="home">

      {/* ─── HERO ─── */}
      <section className="hero section">
        <div className="section-inner hero-inner">
          <div className="hero-content">
            <span className="section-label">Lasting Powers of Attorney</span>
            <h1 className="hero-title">Protect what matters most.</h1>
            <p className="hero-subtitle">Clear. Practical. Trusted.</p>
            <p className="hero-intro">
              Planning ahead protects you, your family and your affairs. A Lasting
              Power of Attorney (LPA) allows you to appoint trusted individuals to
              make decisions on your behalf if you are unable to do so in the future.
              At The LPA Company, we provide a structured, professional service to
              ensure your LPA is properly prepared, correctly executed and validly
              registered.
            </p>
            <div className="hero-actions">
              <button
                className="btn btn-primary"
                onClick={() => setModalOpen(true)}
              >
                Arrange a Consultation
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ marginLeft: 8 }}>
                  <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              <a href="tel:03301334433" className="hero-phone">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6A19.79 19.79 0 012.12 4.18 2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.362 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.338 1.85.573 2.81.7A2 2 0 0122 16.92z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                0330 133 4433
              </a>
              <a href="https://wa.me/message/O2E665HYYHONE1" target="_blank" rel="noopener noreferrer" className="hero-whatsapp">
                <svg width="20" height="20" viewBox="0 0 448 512" fill="currentColor"><path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.3-5-3.7-10.5-6.6z"/></svg>
                WhatsApp
              </a>
            </div>
          </div>

          <div className="hero-stats">
            <div className="stat-card">
              <span className="stat-number">20+</span>
              <span className="stat-label">Years Experience</span>
            </div>
            <div className="stat-card">
              <span className="stat-number" style={{ fontSize: '16px' }}>Personal and professional advice</span>
            </div>
            <div className="stat-card stat-card--accent">
              <span className="stat-quote">"Clear. Practical and Trusted."</span>
            </div>
          </div>
        </div>
      </section>

      {/* ─── WHY ─── */}
      <section className="why section" id="why">
        <div className="section-inner">
          <span className="section-label">The Problem</span>
          <h2 className="section-title">Why put an LPA in place?</h2>

          <div className="why-grid">
            <div className="why-card">
              <div className="why-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                  <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" stroke="var(--accent)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <circle cx="9" cy="7" r="4" stroke="var(--accent)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" stroke="var(--accent)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h3 className="why-card-title">Family</h3>
              <p className="why-card-text">
                Family members may lack legal authority to make decisions.
              </p>
            </div>

            <div className="why-card">
              <div className="why-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                  <rect x="2" y="5" width="20" height="14" rx="2" stroke="var(--accent)" strokeWidth="1.5" />
                  <path d="M2 10h20" stroke="var(--accent)" strokeWidth="1.5" />
                  <path d="M6 15h4" stroke="var(--accent)" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </div>
              <h3 className="why-card-title">Bank Accounts</h3>
              <p className="why-card-text">
                Accounts may become inaccessible without legal authority to act.
              </p>
            </div>

            <div className="why-card">
              <div className="why-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                  <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" stroke="var(--accent)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M9 22V12h6v10" stroke="var(--accent)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h3 className="why-card-title">Property</h3>
              <p className="why-card-text">
                Property transactions may be delayed or impossible to complete.
              </p>
            </div>

            <div className="why-card">
              <div className="why-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="var(--accent)" strokeWidth="1.5" />
                  <path d="M12 6v6l4 2" stroke="var(--accent)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h3 className="why-card-title">Deputyship</h3>
              <p className="why-card-text">
                Court of Protection proceedings are slower, more expensive and
                restrictive.
              </p>
            </div>
          </div>

          <div className="why-callout">
            <p>
              An LPA provides certainty, continuity and legal clarity.
            </p>
          </div>
        </div>
      </section>

      {/* ─── TWO TYPES ─── */}
      <section className="types section" id="types">
        <div className="section-inner">
          <span className="section-label">LPA Types</span>
          <h2 className="section-title">The Two Types of LPAs</h2>
          <p className="section-desc">
            Most clients choose to put both in place, as they deal with different
            areas of decision-making.
          </p>

          <div className="types-grid">
            <div className="type-card">
              <span className="type-tag">Financial</span>
              <h3 className="type-card-title">Property & Financial Affairs LPA</h3>
              <p className="type-card-text">
                Covers decisions about buying and selling property, managing bank
                accounts, paying bills, collecting benefits and handling investments
                and pensions.
              </p>
              <Link to="/property-financial-affairs-lpa" className="learn-more-btn">
                Learn more
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                  <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </Link>
            </div>

            <div className="type-card">
              <span className="type-tag type-tag--health">Health</span>
              <h3 className="type-card-title">Health & Welfare LPA</h3>
              <p className="type-card-text">
                Covers decisions about medical treatment, care arrangements, where
                you live and, if you choose, life-sustaining treatment.
              </p>
              <Link to="/health-welfare-lpa" className="learn-more-btn">
                Learn more
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                  <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ─── PROCESS ─── */}
      <section className="process section" id="process">
        <div className="section-inner">
          <span className="section-label process-label">How It Works</span>
          <h2 className="section-title process-title">From advice to registration</h2>

          <div className="process-grid">
            <div className="process-step">
              <span className="process-number">01</span>
              <h3 className="process-step-title">Initial Consultation</h3>
              <p className="process-step-text">
                We offer zoom, phone or where requested in-person appointments
                by arrangement. We will discuss your personal circumstances and
                objectives and advise.
              </p>
            </div>

            <div className="process-step">
              <span className="process-number">02</span>
              <h3 className="process-step-title">Structuring & Advice</h3>
              <p className="process-step-text">
                We advise on attorney appointments, joint or joint and several
                authority, replacement attorneys and appropriate safeguards.
              </p>
            </div>

            <div className="process-step">
              <span className="process-number">03</span>
              <h3 className="process-step-title">Drafting & Execution</h3>
              <p className="process-step-text">
                We prepare the documentation and guide you through correct signing
                procedures.
              </p>
            </div>

            <div className="process-step">
              <span className="process-number">04</span>
              <h3 className="process-step-title">Registration</h3>
              <p className="process-step-text">
                We prepare and submit the application to the Office of the Public
                Guardian and provide support through the registration process.
              </p>
            </div>
          </div>

          <p className="process-note">
            Our focus is accuracy, compliance and minimising the risk of rejection
            or delay.
          </p>

          <Link to="/mental-capacity-act-2005" className="btn btn-primary process-btn">
            Learn more
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ marginLeft: 8 }}>
              <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </Link>
        </div>
      </section>

      {/* ─── FEES ─── */}
      <section className="fees section" id="fees">
        <div className="section-inner">
          <span className="section-label">Transparent Pricing</span>
          <h2 className="section-title">Fixed fees. No surprises.</h2>

          <div className="fees-grid">
            <div className="fee-card">
              <h3 className="fee-card-title">Single LPA</h3>
              <p className="fee-card-desc">Either type</p>
              <div className="fee-card-price">
                <span className="fee-amount">&pound;199</span>
                <span className="fee-opg">+ &pound;92 OPG fee</span>
              </div>
            </div>

            <div className="fee-card fee-card--popular">
              <span className="fee-badge">Most Popular</span>
              <h3 className="fee-card-title">Both LPAs</h3>
              <p className="fee-card-desc">One person</p>
              <div className="fee-card-price">
                <span className="fee-amount">&pound;349</span>
                <span className="fee-opg">+ &pound;184 OPG fee</span>
              </div>
            </div>

            <div className="fee-card">
              <h3 className="fee-card-title">One Type for a Couple</h3>
              <p className="fee-card-desc">Same type, two people</p>
              <div className="fee-card-price">
                <span className="fee-amount">&pound;349</span>
                <span className="fee-opg">+ &pound;184 OPG fee</span>
              </div>
            </div>

            <div className="fee-card">
              <h3 className="fee-card-title">Both Types for a Couple</h3>
              <p className="fee-card-desc">4 LPAs total</p>
              <div className="fee-card-price">
                <span className="fee-amount">&pound;599</span>
                <span className="fee-opg">+ &pound;368 OPG fee</span>
              </div>
            </div>
          </div>

          <p className="fees-terms">
            50% payable on instruction &middot; Balance and OPG fees payable when
            documents are ready
          </p>
          <p className="fees-terms" style={{ marginTop: 4 }}>
            Additional fees may apply for Certification Services and Certified Copies of Documents.
          </p>

          <Link to="/lpa-fees" className="learn-more-btn">
            Learn more
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </Link>
        </div>
      </section>

      {/* ─── CONTACT ─── */}
      <section className="contact-section section" id="contact">
        <div className="section-inner">
          <span className="section-label">Get Started</span>
          <h2 className="section-title">Ready to discuss your LPA?</h2>

          <div className="contact-grid">
            <div className="contact-left">
              <p className="contact-text">
                Whether you are putting an LPA in place for the first time or
                reviewing existing arrangements, we are here to help. Contact us to
                discuss your requirements and we will guide you through the process.
              </p>
              <button
                className="btn btn-primary"
                onClick={() => setModalOpen(true)}
              >
                Arrange a Consultation
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ marginLeft: 8 }}>
                  <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>

            <div className="contact-card">
              <div className="contact-row">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="var(--accent)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M22 6l-10 7L2 6" stroke="var(--accent)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <div>
                  <span className="contact-row-label">Email</span>
                  <a href="mailto:info@theLPAcompany.com" className="contact-row-value">
                    The LPA Company
                  </a>
                </div>
              </div>

              <div className="contact-row">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6A19.79 19.79 0 012.12 4.18 2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.362 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.338 1.85.573 2.81.7A2 2 0 0122 16.92z" stroke="var(--accent)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <div>
                  <span className="contact-row-label">Phone</span>
                  <a href="tel:03301334433" className="contact-row-value">
                    0330 133 4433
                  </a>
                </div>
              </div>
              <div className="contact-row">
                <svg width="18" height="18" viewBox="0 0 448 512" fill="var(--accent)"><path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z"/></svg>
                <div>
                  <span className="contact-row-label">WhatsApp</span>
                  <a href="https://wa.me/message/O2E665HYYHONE1" target="_blank" rel="noopener noreferrer" className="contact-row-value">
                    Message us on WhatsApp
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── MODAL ─── */}
      <ContactForm isOpen={modalOpen} onClose={() => setModalOpen(false)} />
    </div>
  );
}

export default Home;
